import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRankerDanjiDetail, getRealTransactions, getRealTransactionsMarket } from '../lib/api/ranker-api';
import KbTopIndexChart from '../components/KbTopIndexChart';
import KbTrendComboChart from '../components/KbTrendComboChart';
import KbBuyerSentimentChart from '../components/KbBuyerSentimentChart';
import PresaleSupplyChart from '../components/PresaleSupplyChart';
import MoveinSupplyChart from '../components/MoveinSupplyChart';
import WeeklySnapshotPanel from '../components/WeeklySnapshotPanel';
import InvestmentRecommendationDock from '../components/InvestmentRecommendationDock';
import RealTransactionNewCard from '../components/RealTransactionNewCard';
import DevelopmentInfoCard from '../components/DevelopmentInfoCard';
import SchoolInfoCard from '../components/SchoolInfoCard';
import './ApartmentDetailPage.css';

function ApartmentDetailPage() {
  const TRANSACTION_PAGE_SIZE = 10;
  const SIDE_PAGE_SIZE = 6;

  const navigate = useNavigate();
  const { id: naverDanjiCode } = useParams();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [txError, setTxError] = useState('');
  const [txPeriodYears, setTxPeriodYears] = useState(3);
  const [txMonthlyStats, setTxMonthlyStats] = useState([]);
  const [txChartItems, setTxChartItems] = useState([]);
  const [txMarketItems, setTxMarketItems] = useState([]);
  const [txPtpNo, setTxPtpNo] = useState('');
  const [txDealViewMode, setTxDealViewMode] = useState('sale');

  const txLoadingRef = useRef(false);

  const [railVisibleCount, setRailVisibleCount] = useState(SIDE_PAGE_SIZE);
  const [jiguVisibleCount, setJiguVisibleCount] = useState(SIDE_PAGE_SIZE);
  const [subwayVisibleCount, setSubwayVisibleCount] = useState(SIDE_PAGE_SIZE);
  const [schoolVisibleCount, setSchoolVisibleCount] = useState(SIDE_PAGE_SIZE);
  const [railLoadingMore, setRailLoadingMore] = useState(false);
  const [jiguLoadingMore, setJiguLoadingMore] = useState(false);
  const [subwayLoadingMore, setSubwayLoadingMore] = useState(false);
  const [schoolLoadingMore, setSchoolLoadingMore] = useState(false);

  const [supplyScope, setSupplyScope] = useState('sigungu');

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getRankerDanjiDetail(naverDanjiCode);
        setDetail(response);
      } catch (e) {
        setError(e?.message || '\uB2E8\uC9C0 \uC0C1\uC138 \uC815\uBCF4\uB97C \uC870\uD68C\uD558\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [naverDanjiCode]);

  const loadTransactions = useCallback(async (targetPage, replace = false) => {
    if (!naverDanjiCode || txLoadingRef.current) return;

    txLoadingRef.current = true;
    setTxLoading(true);
    setTxError('');

    try {
      const requestParams = {
        pageNumber: targetPage,
        pageSize: TRANSACTION_PAGE_SIZE,
        periodYears: txPeriodYears > 0 ? txPeriodYears : null,
        ptpNo: txPtpNo || null,
        dealType: txDealViewMode === 'sale' ? 1 : txDealViewMode === 'lease' ? 2 : null,
      };
      let response;
      try {
        response = await getRealTransactionsMarket(naverDanjiCode, requestParams);
      } catch {
        response = await getRealTransactions(naverDanjiCode, requestParams);
      }

      const nextItems = Array.isArray(response?.items) ? response.items : [];
      const nextMonthlyStats = Array.isArray(response?.monthlyStats) ? response.monthlyStats : [];
      const nextChartItems = Array.isArray(response?.chartItems) ? response.chartItems : [];
      const nextMarketItems = Array.isArray(response?.marketItems) ? response.marketItems : [];

      setTransactions(nextItems);
      if (replace || targetPage === 1) {
        setTxMonthlyStats(nextMonthlyStats);
        setTxChartItems(nextChartItems);
        setTxMarketItems(nextMarketItems);
      }
    } catch (e) {
      setTxError(e?.message || '\uC2E4\uAC70\uB798 \uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
    } finally {
      txLoadingRef.current = false;
      setTxLoading(false);
    }
  }, [naverDanjiCode, txPeriodYears, txPtpNo, txDealViewMode]);

  useEffect(() => {
    setTransactions([]);
    setTxMonthlyStats([]);
    setTxChartItems([]);
    setTxMarketItems([]);
    setTxError('');
    loadTransactions(1, true);
  }, [loadTransactions]);

  useEffect(() => {
    setRailVisibleCount(SIDE_PAGE_SIZE);
    setJiguVisibleCount(SIDE_PAGE_SIZE);
    setSubwayVisibleCount(SIDE_PAGE_SIZE);
    setSchoolVisibleCount(SIDE_PAGE_SIZE);
    setRailLoadingMore(false);
    setJiguLoadingMore(false);
    setSubwayLoadingMore(false);
    setSchoolLoadingMore(false);
  }, [detail]);

  const sigunguSupplyRows = useMemo(() => {
    const list = Array.isArray(detail?.supplyMonthlySigungu)
      ? detail.supplyMonthlySigungu
      : Array.isArray(detail?.supplyMonthly) ? detail.supplyMonthly : [];
    return list;
  }, [detail]);

  const sidoSupplyRows = useMemo(() => {
    const list = Array.isArray(detail?.supplyMonthlySido)
      ? detail.supplyMonthlySido
      : [];
    return list;
  }, [detail]);

  const activeSupplyRows = useMemo(
    () => (supplyScope === 'sido' ? (sidoSupplyRows.length > 0 ? sidoSupplyRows : sigunguSupplyRows) : sigunguSupplyRows),
    [sidoSupplyRows, sigunguSupplyRows, supplyScope]
  );

  const fmt = (value, suffix = '') => {
    if (value === null || value === undefined || value === '') return '-';
    return `${value}${suffix}`;
  };

  const fmtNumber = (value, suffix = '') => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return `${value}${suffix}`;
    return `${num.toLocaleString()}${suffix}`;
  };

  const HEAT_SYSTEM_LABEL = {
    HT001: '\uAC1C\uBCC4\uB09C\uBC29',
    HT002: '\uC911\uC559\uB09C\uBC29',
    HT003: '\uAC1C\uBCC4\uC5F4\uC6D0',
    HT004: '\uC911\uC559\uC5F4\uC6D0',
    HT005: '\uC9C0\uC5ED\uB09C\uBC29',
    HT006: '\uC9C0\uC5ED\uC5F4\uC6D0',
    HT000: '\uBBF8\uB178\uCD9C',
  };

  const HEAT_ENERGY_LABEL = {
    HF001: '\uB3C4\uC2DC\uAC00\uC2A4',
    HF002: '\uC5F4\uBCD1\uD569',
    HF003: '\uAE30\uB984',
    HF004: '\uC804\uAE30',
    HF005: '\uC2EC\uC57C\uC804\uAE30',
    HF006: '\uD0DC\uC591\uC5F4',
    HF007: 'LPG',
    HF008: '\uC9C0\uC5F4',
    HF000: '\uBBF8\uB178\uCD9C',
  };

  const fmtUseApproveDate = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const raw = String(value).trim();
    const digits = raw.replace(/\D/g, '');

    if (digits.length >= 8) {
      const yyyy = digits.slice(0, 4);
      const mm = digits.slice(4, 6);
      const dd = digits.slice(6, 8);
      if (Number(mm) >= 1 && Number(mm) <= 12 && Number(dd) >= 1 && Number(dd) <= 31) {
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    if (digits.length >= 6) {
      const yyyy = digits.slice(0, 4);
      const mm = digits.slice(4, 6);
      if (Number(mm) >= 1 && Number(mm) <= 12) {
        return `${yyyy}-${mm}`;
      }
    }

    return raw;
  };

  const fmtHeat = (value, type = 'system') => {
    if (!value) return '-';
    const raw = String(value).trim();
    const map = type === 'energy' ? HEAT_ENERGY_LABEL : HEAT_SYSTEM_LABEL;
    return map[raw] || raw;
  };

  const getLineChipStyle = (color) => {
    const hex = String(color || '').trim();
    if (!/^#([0-9a-fA-F]{6})$/.test(hex)) {
      return { backgroundColor: '#e2e8f0', color: '#334155' };
    }

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return {
      backgroundColor: hex,
      color: luminance > 0.6 ? '#111827' : '#ffffff',
    };
  };

  const loadMoreSideList = (type) => {
    const delayMs = 350;

    if (type === 'rail') {
      setRailLoadingMore(true);
      setTimeout(() => {
        setRailVisibleCount((prev) => prev + SIDE_PAGE_SIZE);
        setRailLoadingMore(false);
      }, delayMs);
      return;
    }

    if (type === 'jigu') {
      setJiguLoadingMore(true);
      setTimeout(() => {
        setJiguVisibleCount((prev) => prev + SIDE_PAGE_SIZE);
        setJiguLoadingMore(false);
      }, delayMs);
      return;
    }

    if (type === 'subway') {
      setSubwayLoadingMore(true);
      setTimeout(() => {
        setSubwayVisibleCount((prev) => prev + SIDE_PAGE_SIZE);
        setSubwayLoadingMore(false);
      }, delayMs);
      return;
    }

    if (type === 'school') {
      setSchoolLoadingMore(true);
      setTimeout(() => {
        setSchoolVisibleCount((prev) => prev + SIDE_PAGE_SIZE);
        setSchoolLoadingMore(false);
      }, delayMs);
    }
  };

  const handlePeriodYearsChange = (years) => {
    if (txPeriodYears === years) return;
    setTxPeriodYears(years);
  };

  const areaOptions = useMemo(() => (
    Array.isArray(detail?.areaOptions) ? detail.areaOptions : []
  ), [detail]);

  const handlePtpNoChange = (nextPtpNo) => {
    setTxPtpNo(nextPtpNo || '');
  };

  const handleTxViewModeChange = (nextMode) => {
    if (!nextMode || txDealViewMode === nextMode) return;
    setTxDealViewMode(nextMode);
  };

  const topTitleRegionName = useMemo(() => {
    const raw = String(detail?.sidoName || detail?.kbRegionMapping?.topRegionNameKo || '').trim();
    return raw;
  }, [detail]);

  const trendTitleRegionName = useMemo(() => {
    const sidoRaw = String(detail?.sidoName || '').trim();
    const sigunguRaw = String(detail?.sigunguName || detail?.kbRegionMapping?.trendRegionNameKo || '').trim();

    if (sidoRaw && sigunguRaw) return `${sidoRaw} ${sigunguRaw}`;
    return sigunguRaw || sidoRaw;
  }, [detail]);

  const supplyRegionName = useMemo(() => {
    if (supplyScope === 'sido') return detail?.sidoName || '\uD574\uB2F9';
    return detail?.sigunguName || detail?.sidoName || '\uD574\uB2F9';
  }, [detail, supplyScope]);

  const weeklyRows = useMemo(() => (
    Array.isArray(detail?.weeklySnapshots) ? detail.weeklySnapshots : []
  ), [detail]);

  const groupedSubways = useMemo(() => {
    const source = Array.isArray(detail?.subways) ? detail.subways : [];
    const map = new Map();

    source.forEach((item) => {
      const stationName = String(item?.stationName || '-').trim() || '-';
      if (!map.has(stationName)) {
        map.set(stationName, {
          stationName,
          lines: [],
          walkingDistance: item?.walkingDistance ?? null,
          walkingDuration: item?.walkingDuration ?? null,
        });
      }

      const row = map.get(stationName);
      const lineName = String(item?.lineName || '').trim();
      const lineColor = String(item?.lineColor || '').trim();
      if (lineName && !row.lines.some((line) => line.lineName === lineName)) {
        row.lines.push({ lineName, lineColor });
      }

      const currentDistance = row.walkingDistance;
      const nextDistance = item?.walkingDistance;
      if (nextDistance !== null && nextDistance !== undefined && (currentDistance === null || currentDistance === undefined || nextDistance < currentDistance)) {
        row.walkingDistance = nextDistance;
        row.walkingDuration = item?.walkingDuration ?? row.walkingDuration;
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const ad = a.walkingDistance ?? Number.POSITIVE_INFINITY;
      const bd = b.walkingDistance ?? Number.POSITIVE_INFINITY;
      return ad - bd;
    });
  }, [detail]);

  if (loading) {
    return (
      <div className="apartment-detail-page">
        <div className="detail-container">
          <p className="status-text">{`\uC0C1\uC138 \uC815\uBCF4\uB97C \uBD88\uB7EC\uC624\uB294 \uC911\uC785\uB2C8\uB2E4...`}</p>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="apartment-detail-page">
        <div className="detail-container">
          <button className="back-btn" onClick={() => navigate(-1)}>
            {`\uB4A4\uB85C\uAC00\uAE30`}
          </button>
          <p className="status-text error">{error || '\uB2E8\uC9C0 \uC815\uBCF4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="apartment-detail-page">
      <div className="detail-container">
        <div className="detail-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            {`\uB4A4\uB85C\uAC00\uAE30`}
          </button>

          <div className="detail-main-grid">
            <div className="left-info-stack">
              <div className="header-info">
                <div className="title-row">
                  <h1>{detail.buildingName || '-'}</h1>
                  <div className="title-right">
                    <div className="grade-badges">
                      {detail.sigunguGrade !== null && detail.sigunguGrade !== undefined ? (
                        <span className="sigungu-grade-badge">{detail.sigunguGrade}{`\uB4F1\uAE09(\uC9C0\uAD6C)`}</span>
                      ) : null}
                      {detail.dongGrade !== null && detail.dongGrade !== undefined ? (
                        <span className="dong-grade-badge">{detail.dongGrade}{`\uB4F1\uAE09(\uB3D9)`}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <p className="address">
                  {[detail.sidoName, detail.sigunguName, detail.bjdName].filter(Boolean).join(' ')}
                  {detail.addressBunji ? ` · ${detail.addressBunji}` : ''}
                </p>

                <div className="info-table-wrap">
                  <table className="info-table">
                    <tbody>
                      <tr>
                        <th>{`\uC138\uB300\uC218`}</th>
                        <td>{detail.familyCnt ? `${detail.familyCnt.toLocaleString()}\uC138\uB300` : '-'}</td>
                        <th>{`\uC0AC\uC6A9\uC2B9\uC778\uC77C`}</th>
                        <td>{fmtUseApproveDate(detail.useApproveDate)}</td>
                      </tr>
                      <tr>
                        <th>{`\uCD1D\uB3D9\uC218`}</th>
                        <td>{fmtNumber(detail.totBldCnt, `\uB3D9`)}</td>
                        <th>{`\uCD5C\uACE0/\uCD5C\uC800\uCE35`}</th>
                        <td>{fmt(detail.maxFloor)}/{fmt(detail.minFloor)}</td>
                      </tr>
                      <tr>
                        <th>{`\uC8FC\uCC28\uB300\uC218`}</th>
                        <td>{fmtNumber(detail.totprkCnt, `\uB300`)}</td>
                        <th>{`\uC138\uB300\uB2F9\uC8FC\uCC28`}</th>
                        <td>{fmt(detail.totprkCntPer)}</td>
                      </tr>
                      <tr>
                        <th>{`\uB09C\uBC29\uBC29\uC2DD`}</th>
                        <td>{fmtHeat(detail.heatType, 'system')}</td>
                        <th>{`\uB09C\uBC29\uC5F0\uB8CC`}</th>
                        <td>{fmtHeat(detail.heatFul, 'energy')}</td>
                      </tr>
                      <tr>
                        <th>{`\uC6A9\uC801\uB960`}</th>
                        <td>{fmt(detail.floorAreaRate, '%')}</td>
                        <th>{`\uAC74\uD3D0\uC728`}</th>
                        <td>{fmt(detail.buildingClosingRate, '%')}</td>
                      </tr>
                      <tr>
                        <th>{`\uC2DC\uACF5\uC0AC`}</th>
                        <td>{fmt(detail.cscoName)}</td>
                        <th>{`\uB2E8\uC9C0 \uD3C9\uD615`}</th>
                        <td>{detail.ptpNames || detail.ptpNoCsv || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="horizontal-card-row">
                <section className="overview-card fixed-height-card">
                  <h3>{`\uC9C0\uD558\uCCA0`}</h3>
                  {groupedSubways.length ? (
                    <>
                      <div className="card-list-scroll">
                        <ul className="bullet-list">
                          {groupedSubways.slice(0, subwayVisibleCount).map((item, idx) => (
                            <li key={`subway-${idx}`}>
                              <div className="subway-row-top">
                                <span>{item.stationName || '-'}</span>
                                <span className="subway-line-badges">
                                  {item.lines.map((line, lineIdx) => (
                                    <span
                                      key={`subway-line-${idx}-${lineIdx}`}
                                      className="subway-line-chip"
                                      style={getLineChipStyle(line.lineColor)}
                                    >
                                      {line.lineName}
                                    </span>
                                  ))}
                                </span>
                              </div>
                              <div className="subway-row-meta">
                                {item.walkingDistance == null ? '-' : `${Number(item.walkingDistance).toLocaleString()}m`}
                                {` · \uB3C4\uBCF4 `}
                                {item.walkingDuration == null ? '-' : item.walkingDuration}
                                {`\uBD84`}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {groupedSubways.length > subwayVisibleCount ? (
                        <button
                          type="button"
                          className="more-btn"
                          onClick={() => loadMoreSideList('subway')}
                          disabled={subwayLoadingMore}
                        >
                          {subwayLoadingMore ? '\uB85C\uB529 \uC911..' : '\uB354\uBCF4\uAE30'}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <p className="empty-mini">{`\uB370\uC774\uD130 \uC5C6\uC74C`}</p>
                  )}
                </section>

                <SchoolInfoCard
                  schools={Array.isArray(detail?.schools) ? detail.schools : []}
                  schoolVisibleCount={schoolVisibleCount}
                  schoolLoadingMore={schoolLoadingMore}
                  onLoadMoreSchool={() => loadMoreSideList('school')}
                />

                <DevelopmentInfoCard
                  developmentRails={Array.isArray(detail?.developmentRails) ? detail.developmentRails : []}
                  railVisibleCount={railVisibleCount}
                  railLoadingMore={railLoadingMore}
                  onLoadMoreRail={() => loadMoreSideList('rail')}
                  developmentJigus={Array.isArray(detail?.developmentJigus) ? detail.developmentJigus : []}
                  jiguVisibleCount={jiguVisibleCount}
                  jiguLoadingMore={jiguLoadingMore}
                  onLoadMoreJigu={() => loadMoreSideList('jigu')}
                />
              </div>
            </div>

            <div className="right-info-stack">
              <RealTransactionNewCard
                transactions={transactions}
                chartItems={txChartItems}
                marketItems={txMarketItems}
                monthlyStats={txMonthlyStats}
                periodYears={txPeriodYears}
                onPeriodYearsChange={handlePeriodYearsChange}
                ptpNo={txPtpNo}
                ptpOptions={areaOptions}
                onPtpNoChange={handlePtpNoChange}
                viewMode={txDealViewMode}
                onViewModeChange={handleTxViewModeChange}
                loading={txLoading}
                error={txError}
              />
            </div>
          </div>
        </div>

        <div className="kb-detail-chart-row">
          <KbTopIndexChart
            regionScopeId={detail?.kbRegionMapping?.topRegionId || undefined}
            titleRegionName={topTitleRegionName}
          />
          <KbTrendComboChart
            regionId={detail?.kbRegionMapping?.trendRegionId || 195}
            titleRegionName={trendTitleRegionName}
          />
          <KbBuyerSentimentChart
            regionId={detail?.kbRegionMapping?.topRegionId || 195}
          />
        </div>

        <WeeklySnapshotPanel rows={weeklyRows} complexName={detail?.buildingName} />

        <div className="sales-section">
          <div className="section-header">
            <h3>{`\uBD84\uC591 \uACF5\uAE09 \uB370\uC774\uD130`}</h3>
            <span className="section-caption">
              {`\uAE30\uC900: \uACFC\uAC70 3\uAC1C\uC6D4 ~ \uD604\uC7AC \uAE30\uC900 + 2\uB144`} ({activeSupplyRows.length}{`\uAC1C\uC6D4`})
            </span>
          </div>

          {activeSupplyRows.length === 0 ? (
            <p className="status-text">{`\uACF5\uAE09 \uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.`}</p>
          ) : (
            <div className="supply-chart-grid">
              <PresaleSupplyChart
                regionName={supplyRegionName}
                scope={supplyScope}
                onScopeChange={setSupplyScope}
                data={activeSupplyRows}
              />
              <MoveinSupplyChart
                regionName={supplyRegionName}
                scope={supplyScope}
                onScopeChange={setSupplyScope}
                data={activeSupplyRows}
              />
            </div>
          )}

          <div className="supply-note">{`\uACF5\uAE09 \uB370\uC774\uD130\uB294 \uAD6C \uB2E8\uC704\uB85C \uBD84\uAE30\uBCC4 \uACF5\uAE09 \uBB3C\uB7C9\uC744 \uC870\uD68C\uD569\uB2C8\uB2E4.`}</div>
        </div>

        <InvestmentRecommendationDock />
      </div>
    </div>
  );
}

export default ApartmentDetailPage;
